<template>
  <div class="Search">
    <transition name="filters">
      <Filters v-if="showFilters" @submitFilters="submitFilters" />
    </transition>
    <Topbar
      title="Rechercher"
      @showFilters="showFilters=true"
      :currentSearchInput="filters.name"
    />

    <section class="Section">
      <SearchInput class="SearchInput" v-model="filters.name" @search="search()" placeholder="Rechercher un vin" />
      <div class="Search__settings">
        <div class="Search__filters" v-if="activeFilterCount>0">
          <a href="#" @click.prevent="showFilters=true" class="Search__filters-count">
            {{ activeFilterCount }} {{ "filtre actif" | pluralize(activeFilterCount) }}
            <span class="Search__filters-details">({{ activeFiltersByName.join(', ') }})</span>
          </a>
          <span @click="resetFilters" class="Search__filters-reset">
            <IconBase width="10" height="10" iconName="close"><IconClose /></IconBase>
          </span>
        </div>

        <a href="#" @click.prevent="showFilters=true" class="Search__filters--desktop">
          <span>Filtrer</span>
          <IconBase width="12" height="12" iconName="filter"><IconFilter /></IconBase>
        </a>

        <div class="Search__sort">
          Trier
          <select v-model="filters.sort" @change="search">
            <option value="pertinence">par pertinence</option>
            <option value="price_asc">par prix croissant</option>
            <option value="price_desc">par prix décroissant</option>
          </select>
        </div>
      </div>
      <div v-if="pageCount > maxPage" class="Search__info">
        Votre recherche retourne plus de {{ maxResults }} résultats, vous pouvez l'affiner avec <a href="#" @click.prevent="showFilters=true">nos filtres</a>
      </div>
      <div v-if="pageCount > 0 || isLoading" class="ProductList" :class="isLoading ? 'is-loading' : ''">
        <router-link :to="{ name: 'Product', params: { id: product.id }}" v-for="product in products" :key="product.id">
          <ProductCard v-bind="product" />
        </router-link>
      </div>
      <div v-if="canLoadMore" class="Search__button-container">
        <button v-if="!isLoadingNext" class="Button Button--primary" @click="getNextProducts">
          Voir plus
        </button>
        <div v-else class="Button__loader"></div>
      </div>
      <div v-if="displaySearchInfoPanel" class="Search__info">
        <div v-if="pageCount > maxPage">
          Votre recherche retourne plus de {{ maxResults }} résultats, vous pouvez l'affiner avec nos filtres <a href="#" @click.prevent="showFilters=true">nos filtres</a>
        </div>
        <div v-if="hasNoResult">Aucune bouteille ne correspond à cette recherche. Essayez d'autres mots clés ou supprimez les filtres de recherche.</div>
        <div v-if="displayWelcomeMessage">
          <p>
            Recherchez une appellation, une couleur, un millésime, un conditionnement…<br /><br /> <a href="#" @click.prevent="showFilters = true">ou utilisez les filtres</a>
          </p>
          <div class="Brand">
            <img
              src="../assets/images/monkey.png"
              alt="Un singe avec un chapeau de marin tenant une bouteille de vin avec une référence à Walt Whitman…"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import productService from "../services/product";
import ProductCard from "../components/ProductCard";
import SearchInput from "../components/SearchInput";
import Filters from "../components/Filters";
import IconClose from "../components/icons/IconClose";
import IconFilter from "../components/icons/IconFilter";

export default {
  data() {
    return {
      products: [],
      isLoading: false,
      showFilters: false,
      pageCount: 0,
      currentPage: 1,
      maxPage: 3,
      hasDirtyFilters: false,
      isLoadingNext: false,
      productPerPage: 40
    };
  },
  computed: {
    ...mapState("search", ["filterOptions", "filters"]),
    ...mapGetters("search", ["activeFilterCount", "activeFiltersByName"]),
    hasNoResult() {
      return this.pageCount === 0 && (this.activeFilterCount > 0 || this.filters.name !== "");
    },
    displayWelcomeMessage() {
      return this.pageCount === 0 && !this.hasNoResult;
    },
    displaySearchInfoPanel() {
      return !this.isLoading && !this.hasDirtyFilters && (this.pageCount > this.maxPage || this.hasNoResult || this.displayWelcomeMessage);
    },
    maxResults() {
      return this.maxPage * this.productPerPage;
    },
    canLoadMore() {
      return !this.isLoading && this.currentPage < this.pageCount;
    }
  },
  watch: {
    $route() {
      this.$store.commit("search/setFilters", {
        ...this.filters,
        ...this.$route.query
      });

      this.getFirstProducts();
    },
    filters: {
      handler() {
        this.hasDirtyFilters = true;
      },
      deep: true
    }
  },
  methods: {
    getFirstProducts() {
      this.products = [];
      this.isLoading = true;

      productService.getAll({ ...this.filters, limit: this.productPerPage })
        .then(({ data: products, headers }) => {
          this.pageCount = parseInt(headers["x-page-count"]);
          this.products = products;
          this.isLoading = false;
          this.hasDirtyFilters = false;
        });
    },
    getNextProducts() {
      this.isLoadingNext = true;
      this.currentPage++;

      productService.getAll({ ...this.filters, limit: this.productPerPage, page: this.currentPage })
        .then(({ data: products, headers }) => {
          this.products = this.products.concat(products);
          this.isLoadingNext = false;
        });
    },
    search() {
      this.$router.push({
        name: "Search",
        query: { ...this.filters }
      }).catch(err => {});
    },
    submitFilters() {
      this.showFilters = false;
      this.search();
    },
    resetFilters() {
      this.$store.commit("search/resetFilters");
      this.search();
    }
  },
  created() {
    this.$store.dispatch("search/getFilterOptions");
    this.showFilters = !!this.$route.query.showFilters;

    if (Object.keys(this.$route.query).length > 0) {
      this.$store.commit("search/setFilters", {
        ...this.filters,
        ...this.$route.query
      });

      if (!this.showFilters) {
        this.getFirstProducts();
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit("search/resetFilters");
    next();
  },
  components: {
    ProductCard,
    SearchInput,
    Filters,
    IconClose,
    IconFilter
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/_variables.scss';

.Search {
  &__info {
    text-align: center;
    padding: $global-margin*2 $global-margin;
    margin: $global-margin 0;
    color: $color-grey;
    line-height: 1.2;
    background-color: #fff;

    a {
      color: $color-grey;
      text-decoration: underline;
    }
  }

  &__settings {
    font-size: 0.65rem;
    display: flex;
    align-items: center;
    // justify-content: space-between;
  }

  &__filters {
    display: inline-flex;
    align-items: center;
    font-weight: 700;
    background: $color-grey;
    color: #fff;
    border-radius: $global-radius;

    &--desktop {
      text-transform: uppercase;
      margin-left: auto;
      font-weight: 700;
      color: $color-grey;

      span {
        margin-right: 5px;
      }

      @media (max-width: 900px) {
        display: none;
      }
    }

    &-count {
      padding: 0.3rem 0.4rem;
      color: $color-smoke;
    }

    &-details {
      @media (max-width: 900px) {
        display: none;
      }
    }

    &-reset {
      padding: 0 0.2rem 0;
      margin-right: 0.2rem;
    }
  }

  &__sort {
    text-transform: uppercase;
    margin-left: $global-margin;
    font-weight: 700;
    color: $color-grey;

    @media (max-width: 900px) {
      margin-left: auto;
    }

    select {
      border: none;
      color: $color-grey;
      text-transform: uppercase;
      font-size: 1em;
      appearance: none;
      font-weight: 700;
      background-color: transparent;
      background-repeat: no-repeat;
      background-position: center right;
      padding-right: 1rem;
      background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="15" fill="%2393A9C0" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0z" fill="none"/><path d="M7 10l5 5 5-5z"/></svg>');
    }
  }

  &__button-container {
    display: flex;
    justify-content: center;
    margin-top: $global-margin;
  }
}

@media (min-width: 900px) {
  .Search__settings {
    font-size: 0.875rem;
  }

  .SearchInput {
    display: none;
  }
}

.filters-enter-active,
.filters-leave-active {
  transition: all 0.16s ease-in-out;
}

.filters-enter,
.filters-leave-to {
  transition: 0.05s;
  opacity: 0;
  transform: translate(90%);
}
</style>
